// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Gauge, Loader2, AlertCircle, RefreshCw, Save, Info } from 'lucide-react'
import * as api from '../../api/client'
import { AdminOverviewPanel, AdminDisclosuresPanel } from './DataApiAdminPanels'
import SearchableSelect from '../../components/common/SearchableSelect'

/** Server field names -> the label on screen. The 422 body carries a stable machine name by
 *  design; pasting `max_wakes_per_day` in front of an operator is the UI's mistake, not the API's. */
const FIELD_LABELS: Record<string, string> = {
  max_clients: 'Max clients',
  max_keys_per_client: 'Max keys per client',
  max_webhooks: 'Max webhooks',
  max_rows_per_day: 'Max rows / day',
  max_wakes_per_day: 'Max wakes / day',
}

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'

/**
 * D-API-1 Phase 3c — the SaaS-admin surface for Data API governance.
 *
 * Before this there was none: `DataApiAdminSetLimits` existed with zero UI callers, so setting a
 * pharmacy's caps was a curl job, `max_webhooks` had no setter at all, and the Phase 3b budgets
 * were reachable only by hand-written SQL.
 *
 * The number this page exists for is `tenant.wakes`. A wake blocks auto-pause for 60 minutes, so it
 * is what a partner actually COSTS — and until the meter shipped, nothing attributed it per client.
 */
type AdminTab = 'overview' | 'budgets' | 'usage' | 'disclosures'

const ADMIN_TABS: { id: AdminTab; label: string }[] = [
  { id: 'overview', label: 'Partners & control' },
  { id: 'budgets', label: 'Budgets' },
  { id: 'usage', label: 'Usage' },
  { id: 'disclosures', label: 'Disclosures' },
]

export default function DataApiGovernanceTab() {
  const [pharmacies, setPharmacies] = useState<Array<{ id: string; name: string }>>([])
  const [pharmacyId, setPharmacyId] = useState('')
  const [data, setData] = useState<api.DataApiGovernance | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const bannerRef = useRef<HTMLDivElement | null>(null)

  // U1: the banner renders at the top of a long form while the button that triggered it is far
  // below, so a failed save could leave the operator staring at an unchanged form with no visible
  // reason. role="alert" already serves a screen reader — it was the sighted mouse user who was
  // served worst, which is backwards. Scroll it into view and move focus to it.
  useEffect(() => {
    if (!error && !notice) return
    bannerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    bannerRef.current?.focus()
  }, [error, notice])

  // Empty string = "leave unchanged", which is exactly what the PATCH means, so the form state maps
  // one-to-one onto the request rather than needing a diff against the loaded values.
  const [form, setForm] = useState({ clients: '', keys: '', webhooks: '', rows: '', wakes: '' })

  // CHILD TABS (owner, 2026-09-18). Budgets and a seven-day usage table were stacked on one page
  // with the partner detail nowhere at all. Each of these is a different question — "what exists",
  // "what are they allowed", "what did they use", "what did they see" — so each gets its own tab.
  const [tab, setTab] = useState<AdminTab>('overview')

  useEffect(() => { void loadPharmacies() }, [])
  useEffect(() => { if (pharmacyId) void load(pharmacyId) }, [pharmacyId])

  async function loadPharmacies() {
    setLoading(true); setError(null)
    try {
      const list = await api.listPharmacies()
      const rows = list.items.map(p => ({ id: p.id, name: p.name })).sort((a, b) => a.name.localeCompare(b.name))
      setPharmacies(rows)
      if (rows.length && !pharmacyId) setPharmacyId(rows[0].id)
    } catch (e: any) { setError(e?.body?.message || e?.message || 'Could not load pharmacies') }
    finally { setLoading(false) }
  }

  async function load(id: string) {
    setLoading(true); setError(null); setNotice(null)
    try {
      const d = await api.getDataApiGovernance(id)
      setData(d)
      setForm({ clients: '', keys: '', webhooks: '', rows: '', wakes: '' })
    } catch (e: any) { setError(e?.body?.message || e?.message || 'Could not load limits') }
    finally { setLoading(false) }
  }

  async function save() {
    if (!pharmacyId) return
    setSaving(true); setError(null); setNotice(null)
    try {
      const n = (v: string) => (v.trim() === '' ? undefined : Number(v))
      await api.setDataApiLimits(pharmacyId, {
        maxClients: n(form.clients), maxKeysPerClient: n(form.keys), maxWebhooks: n(form.webhooks),
        maxRowsPerDay: n(form.rows), maxWakesPerDay: n(form.wakes),
      })
      // Reload first, THEN set the notice: load() clears both banners on entry, so setting the
      // notice before it meant the confirmation was wiped by the refresh that followed (review U4).
      await load(pharmacyId)
      setNotice('Limits saved.')
    } catch (e: any) {
      // The server answers 422 with { field, message }. Showing that beats a generic failure — but
      // show the LABEL the operator is looking at, not the column name. The server is right to
      // return a stable machine name; the UI is wrong to paste it on screen (review U2).
      const b = e?.body
      setError(b?.field ? `${FIELD_LABELS[b.field] ?? b.field}: ${b.message}` : (b?.message || e?.message || 'Could not save'))
    } finally { setSaving(false) }
  }

  async function clearBudgets() {
    if (!pharmacyId) return
    if (!confirm('Remove both daily budgets for this pharmacy?\n\nRow and wake limits stop applying; per-key limits are unaffected.')) return
    setSaving(true); setError(null); setNotice(null)
    try {
      await api.setDataApiLimits(pharmacyId, { clearBudgets: true })
      await load(pharmacyId)
      setNotice('Budgets removed.')
    } catch (e: any) { setError(e?.body?.message || e?.message || 'Could not clear budgets') }
    finally { setSaving(false) }
  }

  // Usage arrives as (client, metric) rows; the readable shape is one row per client.
  const byClient = useMemo(() => {
    const m = new Map<string, { name: string; metrics: Record<string, number> }>()
    for (const u of data?.usage_7d ?? []) {
      const key = u.client_id
      if (!m.has(key)) m.set(key, { name: u.client_name || '(deleted client)', metrics: {} })
      m.get(key)!.metrics[u.metric] = u.total
    }
    return [...m.entries()].map(([id, v]) => ({ id, ...v }))
  }, [data])

  const wakesTotal = useMemo(
    () => byClient.reduce((sum, c) => sum + (c.metrics['tenant.wakes'] ?? 0), 0),
    [byClient])

  const L = data?.limits

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
            <Gauge size={18} aria-hidden="true" /> Data API governance
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: MUTE }}>
            Per-pharmacy caps and what each partner actually costs. A tenant wake blocks auto-pause for
            60 minutes, so <strong>wakes</strong> — not rows — is the number that drives the bill.
          </p>
        </div>
        <button type="button" onClick={() => pharmacyId && load(pharmacyId)} disabled={loading || saving}
          style={btn} aria-label="Reload limits and usage">
          <RefreshCw size={14} aria-hidden="true" /> Refresh
        </button>
      </header>

      {/* Typeahead rather than a plain dropdown: this list is every pharmacy on the platform and
          grows with the business, which is the case the searchable-dropdown gate exists for. */}
      <label style={{ ...lbl, display: 'block', marginBottom: 6 }}>Pharmacy</label>
      <div style={{ maxWidth: 420, marginBottom: 18 }}>
        <SearchableSelect
          value={pharmacyId}
          onChange={setPharmacyId}
          ariaLabel="Pharmacy"
          placeholder={pharmacies.length === 0 ? 'Loading pharmacies…' : 'Select a pharmacy…'}
          options={pharmacies.map(p => ({ value: p.id, label: p.name }))}
        />
      </div>

      {error && (
        <div ref={bannerRef} role="alert" tabIndex={-1} style={banner('#fee2e2', '#b91c1c')}>
          <AlertCircle size={14} aria-hidden="true" /> {error}
        </div>
      )}
      {notice && <div ref={bannerRef} role="status" tabIndex={-1} style={banner('#dcfce7', '#15803d')}>{notice}</div>}

      {loading && <p style={{ color: MUTE, fontSize: 13 }}><Loader2 size={14} className="spin" aria-hidden="true" /> Loading…</p>}

      {!loading && L && (
        <>
          <div role="tablist" aria-label="Data API governance" style={tabBar}>
            {ADMIN_TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                id={`gov-tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`gov-panel-${t.id}`}
                tabIndex={tab === t.id ? 0 : -1}
                onClick={() => setTab(t.id)}
                onKeyDown={e => {
                  const i = ADMIN_TABS.findIndex(x => x.id === tab)
                  if (e.key === 'ArrowRight') setTab(ADMIN_TABS[(i + 1) % ADMIN_TABS.length].id)
                  if (e.key === 'ArrowLeft') setTab(ADMIN_TABS[(i - 1 + ADMIN_TABS.length) % ADMIN_TABS.length].id)
                }}
                style={tab === t.id ? tabOn : tabOff}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div role="tabpanel" id={`gov-panel-${tab}`} aria-labelledby={`gov-tab-${tab}`} tabIndex={0} style={{ outline: 'none' }}>
            {tab === 'overview' && <AdminOverviewPanel pharmacyId={pharmacyId} onError={setError} />}

            {tab === 'budgets' && (<>
              <section aria-labelledby="gov-limits" style={card}>
                <h3 id="gov-limits" style={h3}>Limits</h3>
                <p style={{ ...hint, marginTop: 0 }}>
                  <Info size={12} aria-hidden="true" /> Leave a box empty to keep the current value.
                  {!L.configured && ' This pharmacy has no saved row yet, so the values shown are platform defaults.'}
                </p>

                <div style={grid}>
                  <Field id="f-clients"  label="Max clients"          current={L.max_clients}
                         value={form.clients}  onChange={v => setForm({ ...form, clients: v })} />
                  <Field id="f-keys"     label="Max keys per client"  current={L.max_keys_per_client}
                         value={form.keys}     onChange={v => setForm({ ...form, keys: v })} />
                  <Field id="f-webhooks" label="Max webhooks"         current={L.max_webhooks}
                         value={form.webhooks} onChange={v => setForm({ ...form, webhooks: v })}
                         hint="Previously fixed at 5 for every pharmacy." />
                </div>

                <h3 style={{ ...h3, marginTop: 22 }}>Daily budgets</h3>
                <p style={hint}>
                  Aggregate across <strong>all</strong> of this pharmacy&rsquo;s partners. Per-key limits cannot see
                  this: three partners each inside their own limit still wake the same tenant database.
                  Leave empty for no budget.
                </p>
                <div style={grid}>
                  <Field id="f-rows"  label="Max rows / day"  current={L.max_rows_per_day}
                         value={form.rows}  onChange={v => setForm({ ...form, rows: v })} />
                  <Field id="f-wakes" label="Max wakes / day" current={L.max_wakes_per_day}
                         value={form.wakes} onChange={v => setForm({ ...form, wakes: v })}
                         hint="Once spent, pull requests are refused for EVERY client of this pharmacy." />
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                  <button type="button" onClick={save} disabled={saving} style={btnPrimary}>
                    <Save size={14} aria-hidden="true" /> {saving ? 'Saving…' : 'Save limits'}
                  </button>
                  {(L.max_rows_per_day != null || L.max_wakes_per_day != null) && (
                    <button type="button" onClick={clearBudgets} disabled={saving} style={btnDanger}>
                      Remove budgets
                    </button>
                  )}
                </div>
              </section>
            </>)}

            {tab === 'usage' && (<>
              <section aria-labelledby="gov-usage" style={{ ...card, marginTop: 18 }}>
                <h3 id="gov-usage" style={h3}>Usage — last 7 days</h3>
                {byClient.length === 0 ? (
                  <p style={{ color: MUTE, fontSize: 13, margin: 0 }}>
                    No metered activity. Counters start when a partner calls the API or an envelope is delivered.
                  </p>
                ) : (
                  <>
                    <p style={hint}>
                      <strong>{wakesTotal}</strong> tenant wake{wakesTotal === 1 ? '' : 's'} attributed in this window
                      {L.max_wakes_per_day != null && <> · daily budget {L.max_wakes_per_day}</>}
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: MUTE, paddingTop: 6 }}>
                        Totals per partner over the trailing 7 days, from the usage meter.
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col" style={th}>Partner</th>
                          <th scope="col" style={thNum}>Wakes</th>
                          <th scope="col" style={thNum}>Requests</th>
                          <th scope="col" style={thNum}>Rows</th>
                          <th scope="col" style={thNum}>Denied</th>
                          <th scope="col" style={thNum}>Deliveries</th>
                          <th scope="col" style={thNum}>Attempts</th>
                          <th scope="col" style={thNum}>Capped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byClient.map(c => (
                          <tr key={c.id}>
                            <th scope="row" style={{ ...td, fontWeight: 600, textAlign: 'left' }}>{c.name}</th>
                            <td style={tdNum}><strong>{c.metrics['tenant.wakes'] ?? 0}</strong></td>
                            <td style={tdNum}>{c.metrics['pull.requests'] ?? 0}</td>
                            <td style={tdNum}>{c.metrics['pull.rows'] ?? 0}</td>
                            <td style={tdNum}>{c.metrics['pull.denied'] ?? 0}</td>
                            <td style={tdNum}>{c.metrics['hook.deliveries'] ?? 0}</td>
                            <td style={tdNum}>{c.metrics['hook.attempts'] ?? 0}</td>
                            <td style={tdNum}>{c.metrics['hook.capped'] ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </section>
            </>)}

            {tab === 'disclosures' && <AdminDisclosuresPanel pharmacyId={pharmacyId} onError={setError} />}
          </div>
        </>
      )}
    </div>
  )
}

function Field({ id, label, current, value, onChange, hint: fieldHint }: {
  id: string; label: string; current: number | null; value: string
  onChange: (v: string) => void; hint?: string
}) {
  return (
    <div>
      <label htmlFor={id} style={lbl}>{label}</label>
      <input id={id} type="number" min={0} value={value} onChange={e => onChange(e.target.value)}
        placeholder={current == null ? 'not set' : String(current)}
        aria-describedby={fieldHint ? `${id}-hint` : undefined} style={input} />
      <div style={{ fontSize: 11, color: MUTE, marginTop: 4 }}>
        Current: <strong>{current == null ? 'not set' : current}</strong>
      </div>
      {fieldHint && <div id={`${id}-hint`} style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>{fieldHint}</div>}
    </div>
  )
}

const tabBar: CSSProperties = { display: 'flex', gap: 4, borderBottom: `1px solid ${BORDER}`, margin: '0 0 16px' }
const tabBase: CSSProperties = { padding: '8px 15px', border: 'none', background: 'transparent', fontSize: 13, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 }
const tabOn: CSSProperties = { ...tabBase, fontWeight: 700, color: 'var(--color-primary, #1d4ed8)', borderBottomColor: 'var(--color-primary, #1d4ed8)' }
const tabOff: CSSProperties = { ...tabBase, color: MUTE }

const card: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16 }
const h3: CSSProperties = { fontSize: 14, margin: '0 0 8px', fontWeight: 700 }
const hint: CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', fontSize: 12, color: MUTE, margin: '0 0 12px' }
const grid: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }
const lbl: CSSProperties = { fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }
const input: CSSProperties = { width: '100%', padding: '7px 10px', border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 13 }
const btn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: `1px solid ${BORDER}`, borderRadius: 7, background: 'transparent', fontSize: 13, cursor: 'pointer' }
const btnPrimary: CSSProperties = { ...btn, background: 'var(--color-primary, #1d4ed8)', color: '#fff', borderColor: 'transparent', fontWeight: 600 }
const btnDanger: CSSProperties = { ...btn, color: 'var(--color-danger, #b91c1c)', borderColor: 'var(--color-danger, #b91c1c)' }
const th: CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${BORDER}`, fontSize: 11, textTransform: 'uppercase', color: MUTE }
const thNum: CSSProperties = { ...th, textAlign: 'right' }
const td: CSSProperties = { padding: '6px 8px', borderBottom: `1px solid ${BORDER}` }
const tdNum: CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

function banner(bg: string, fg: string): CSSProperties {
  return { display: 'flex', alignItems: 'center', gap: 6, background: bg, color: fg, padding: '8px 12px', borderRadius: 7, fontSize: 13, marginBottom: 14 }
}
